import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid'
import { LS_TABLE_PREFERENCES } from 'constants/localStorageKeys'
import { getCurrentToken, getTokenContent } from 'tools/common'

export const CURRENT_TABLE_PREFERENCES_VERSION = 1 as const

const ACTIONS_FIELD = 'actions'

export type TablePreferences = {
  version: typeof CURRENT_TABLE_PREFERENCES_VERSION
  columnWidths?: Record<string, number>
  columnOrder?: string[]
  columnVisibilityModel?: GridColumnVisibilityModel
  paginationLimit?: number
}

type TablePreferencesEntry = {
  userId: string
  factorId: string
  tableId: string
  preferences: TablePreferences
}

type TablePreferencesRoot = {
  version: typeof CURRENT_TABLE_PREFERENCES_VERSION
  entries: TablePreferencesEntry[]
}

const emptyPreferences = (): TablePreferences => ({ version: CURRENT_TABLE_PREFERENCES_VERSION })

const emptyRoot = (): TablePreferencesRoot => ({ version: CURRENT_TABLE_PREFERENCES_VERSION, entries: [] })

type PreferencesScope = { userId: string; factorId: string }

const getScope = (): PreferencesScope | null => {
  try {
    const token = getCurrentToken()
    if (!token) return null
    const content = getTokenContent(token)
    const userId = content?.userId
    const factorId = content?.factorId
    if (userId === undefined || userId === null || factorId === undefined || factorId === null) {
      return null
    }
    return { userId: String(userId), factorId: String(factorId) }
  } catch {
    return null
  }
}

const readRoot = (): TablePreferencesRoot => {
  try {
    const raw = localStorage.getItem(LS_TABLE_PREFERENCES)
    if (!raw) return emptyRoot()
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.version !== CURRENT_TABLE_PREFERENCES_VERSION || !Array.isArray(parsed.entries)) {
      return emptyRoot()
    }
    return { version: CURRENT_TABLE_PREFERENCES_VERSION, entries: parsed.entries.filter(isValidEntry) }
  } catch {
    return emptyRoot()
  }
}

const writeRoot = (root: TablePreferencesRoot): void => {
  try {
    localStorage.setItem(LS_TABLE_PREFERENCES, JSON.stringify(root))
  } catch {
    return
  }
}

const isValidEntry = (entry: any): entry is TablePreferencesEntry =>
  Boolean(entry) &&
  typeof entry === 'object' &&
  typeof entry.userId === 'string' &&
  typeof entry.factorId === 'string' &&
  typeof entry.tableId === 'string' &&
  Boolean(entry.preferences) &&
  typeof entry.preferences === 'object' &&
  (entry.preferences.columnOrder === undefined || Array.isArray(entry.preferences.columnOrder)) &&
  (entry.preferences.columnWidths === undefined ||
    (typeof entry.preferences.columnWidths === 'object' && entry.preferences.columnWidths !== null)) &&
  (entry.preferences.columnVisibilityModel === undefined ||
    (typeof entry.preferences.columnVisibilityModel === 'object' && entry.preferences.columnVisibilityModel !== null)) &&
  (entry.preferences.paginationLimit === undefined || typeof entry.preferences.paginationLimit === 'number')

const matchesScope = (entry: TablePreferencesEntry, scope: PreferencesScope, tableId: string): boolean =>
  entry.userId === scope.userId && entry.factorId === scope.factorId && entry.tableId === tableId

export const getTablePreferences = (tableId?: string): TablePreferences => {
  if (!tableId) return emptyPreferences()
  const scope = getScope()
  if (!scope) return emptyPreferences()

  const root = readRoot()
  const entry = root.entries.find((item) => matchesScope(item, scope, tableId))
  if (!entry || !entry.preferences) return emptyPreferences()

  return { ...emptyPreferences(), ...entry.preferences, version: CURRENT_TABLE_PREFERENCES_VERSION }
}

const updateTablePreferences = (
  tableId: string,
  updater: (previous: TablePreferences) => TablePreferences
): void => {
  const scope = getScope()
  if (!scope) return

  const root = readRoot()
  const index = root.entries.findIndex((item) => matchesScope(item, scope, tableId))
  const previous = index >= 0 ? root.entries[index].preferences : emptyPreferences()
  const nextPreferences: TablePreferences = { ...updater(previous), version: CURRENT_TABLE_PREFERENCES_VERSION }

  const entry: TablePreferencesEntry = {
    userId: scope.userId,
    factorId: scope.factorId,
    tableId,
    preferences: nextPreferences,
  }

  if (index >= 0) {
    root.entries[index] = entry
  } else {
    root.entries.push(entry)
  }

  writeRoot(root)
}

export const saveColumnWidth = (tableId: string | undefined, field: string, width: number): void => {
  if (!tableId || !field || field === ACTIONS_FIELD) return
  updateTablePreferences(tableId, (previous) => ({
    ...previous,
    columnWidths: { ...(previous.columnWidths ?? {}), [field]: Math.round(width) },
  }))
}

export const mergeColumnOrder = (previousOrder: string[], newOrder: string[]): string[] => {
  const newOrderSet = new Set(newOrder)
  const result = [...newOrder]
  let anchorIndex = -1
  previousOrder.forEach((field) => {
    if (newOrderSet.has(field)) {
      anchorIndex = result.indexOf(field)
      return
    }
    const insertAt = anchorIndex + 1
    result.splice(insertAt, 0, field)
    anchorIndex = insertAt
  })
  return result
}

export const saveColumnOrder = (tableId: string | undefined, order: string[]): void => {
  if (!tableId) return
  const cleaned = order.filter((field) => Boolean(field) && field !== ACTIONS_FIELD)
  updateTablePreferences(tableId, (previous) => ({
    ...previous,
    columnOrder: mergeColumnOrder(previous.columnOrder ?? [], cleaned),
  }))
}

export const saveColumnVisibilityModel = (
  tableId: string | undefined,
  model: GridColumnVisibilityModel
): void => {
  if (!tableId) return
  const { [ACTIONS_FIELD]: _actions, ...rest } = model
  updateTablePreferences(tableId, (previous) => ({ ...previous, columnVisibilityModel: rest }))
}

export const savePaginationLimit = (tableId: string | undefined, paginationLimit: number): void => {
  if (!tableId) return
  const numericLimit = Number(paginationLimit)
  if (Number.isNaN(numericLimit)) return
  updateTablePreferences(tableId, (previous) => ({ ...previous, paginationLimit: numericLimit }))
}

export const mergeColumnsWithPreferences = <T extends GridColDef>(
  columns: T[],
  preferences: TablePreferences
): T[] => {
  const actionColumns = columns.filter((column) => column.field === ACTIONS_FIELD)
  const dataColumns = columns.filter((column) => column.field !== ACTIONS_FIELD)

  const { columnWidths, columnOrder } = preferences

  let resized = dataColumns
  if (columnWidths) {
    resized = dataColumns.map((column) => {
      if (column.field && columnWidths[column.field] != null) {
        return { ...column, width: columnWidths[column.field], flex: undefined }
      }
      return column
    })
  }

  let ordered = resized
  if (columnOrder && columnOrder.length > 0) {
    const orderIndex = new Map(columnOrder.map((field, index) => [field, index]))
    const known = resized.filter((column) => column.field && orderIndex.has(column.field))
    const unknown = resized.filter((column) => !column.field || !orderIndex.has(column.field))
    known.sort((a, b) => (orderIndex.get(a.field!) ?? 0) - (orderIndex.get(b.field!) ?? 0))
    ordered = [...known, ...unknown]
  }

  return [...ordered, ...actionColumns]
}
